
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { 
  FacebookShareButton, LinkedinShareButton, TwitterShareButton, 
  FacebookIcon, LinkedinIcon, TwitterIcon
} from 'react-share';
import Certificate from './Certificate';

interface QuizCompletedProps {
  skill: string;
  score: number;
  passed: boolean;
  onComplete: () => void;
}

const QuizCompleted: React.FC<QuizCompletedProps> = ({ skill, score, passed, onComplete }) => {
  const { toast } = useToast();
  const shareUrl = window.location.href;
  const shareTitle = passed 
    ? `I passed the ${skill} skill test with a score of ${score}%!` 
    : `I completed the ${skill} skill test!`;

  const handlePrintCertificate = () => {
    if (!passed) {
      toast({
        title: "Not Eligible",
        description: "You need to score at least 90% to get a certificate.",
        variant: "destructive"
      });
      return;
    }
    
    const certificateElement = document.getElementById('certificate');
    if (certificateElement) {
      const printWindow = window.open('', '', 'height=650,width=900');
      if (printWindow) {
        printWindow.document.write('<html><head><title>Certificate</title>');
        printWindow.document.write('<style>body { margin: 0; padding: 20px; }</style>');
        printWindow.document.write('</head><body>');
        printWindow.document.write(certificateElement.outerHTML);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.focus();
        
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      }
    }
  };

  return (
    <Card className="border border-[#0EA5E9]/20 shadow-md bg-white/80">
      <CardHeader>
        <CardTitle>Quiz Completed</CardTitle>
        <CardDescription>
          Thank you for completing the {skill} quiz.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col items-center justify-center py-6 space-y-6">
        <div className="text-center">
          <h3 className="text-2xl font-bold mb-2">Your Score: {score}%</h3>
          <p className="text-gray-600 mb-4">
            {passed 
              ? "Congratulations! You've passed the test." 
              : "You need 90% or higher to pass this test."}
          </p>
          
          {passed && (
            <div className="mb-6" style={{ display: 'none' }}>
              <Certificate id="certificate" userName="Test User" skill={skill} score={score} />
            </div>
          )}
          
          <div className="flex flex-col gap-4">
            {passed && (
              <Button 
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={handlePrintCertificate}
              >
                Download Certificate
              </Button>
            )}
            
            <Button 
              className="bg-[#0EA5E9] text-white hover:bg-[#0EA5E9]/90"
              onClick={onComplete}
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
        
        <div className="border-t border-gray-200 pt-6 w-full">
          <h4 className="text-center mb-4 font-medium">Share your achievement</h4>
          <div className="flex justify-center gap-4">
            <LinkedinShareButton url={shareUrl} title={shareTitle}>
              <LinkedinIcon size={40} round />
            </LinkedinShareButton>
            
            <TwitterShareButton url={shareUrl} title={shareTitle}>
              <TwitterIcon size={40} round />
            </TwitterShareButton>
            
            <FacebookShareButton url={shareUrl} title={shareTitle}>
              <FacebookIcon size={40} round />
            </FacebookShareButton>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default QuizCompleted;
